import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'utils/highchartsExporting';
import { buildScatterChartOptions, buildCategoryChartOptions } from './chartUtils';
import Spinner from '../../../components/widgets/Spinner';
import CreativeChartHeader from './CreativeChartHeader';
import css from './CreativeChart.css';
import { COLUMN_CHART, LINE_CHART, SCATTER_CHART, BAR_CHART } from '../../../utils/dashboardConsts';
import { WIDGET_TYPE_TO_SCHEMA } from '../../../components/partials/ChartWidgetWrapper/consts';
import { EmptyState } from '../../../components/widgets';
import { GalleryContext, Dimensions } from '../../consts';
import { SHOW_ALL } from '../../../components/partials/TopXChartDataFilter';
import { BreakdownType } from './consts';

export const TOP_X_THRESHOLD = 5;
const BREAKDOWN_CHART_NO_DATA = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ANALYSIS.BREAKDOWN_CHART_NO_DATA';
const PERFORMANCE_CHART_NO_DATA = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ANALYSIS.PERFORMANCE_CHART_NO_DATA';
const SCATTER_CHART_NO_DATA = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.ANALYSIS.SCATTER_CHART_NO_DATA';

const hasMultipleCreatives = data => {
    if (!data?.length) return false;

    const firstHash = data[0][Dimensions.CREATIVE_IMAGE_HASH];
    return data.some(row => row[Dimensions.CREATIVE_IMAGE_HASH] !== firstHash);
};

const CHART_TYPE_CONFIG = {
    [COLUMN_CHART]: {
        xAxisLabelRotation: 0,
        emptyStateIcon: WIDGET_TYPE_TO_SCHEMA.column,
        emptyStateMessage: BREAKDOWN_CHART_NO_DATA,
    },
    [LINE_CHART]: {
        xAxisLabelRotation: -45,
        emptyStateIcon: WIDGET_TYPE_TO_SCHEMA.line,
        emptyStateMessage: PERFORMANCE_CHART_NO_DATA,
    },
    [SCATTER_CHART]: {
        xAxisLabelRotation: 0,
        emptyStateIcon: WIDGET_TYPE_TO_SCHEMA.line,
        emptyStateMessage: SCATTER_CHART_NO_DATA,
    },
    [BAR_CHART]: {
        xAxisLabelRotation: 0,
        emptyStateIcon: WIDGET_TYPE_TO_SCHEMA.column,
        emptyStateMessage: BREAKDOWN_CHART_NO_DATA,
    },
};

const CreativeChart = ({
    dropdowns = [],
    data = [],
    loading = false,
    title,
    chartType,
    breakdownsOptions = [],
    selectedBreakdown,
    onBreakdownChange,
    breakdownsInputType = BreakdownType.TABS,
    breakdownsDropdownLabel,
    showTopX = false,
    buildTooltipFormatter,
    hidingChartMessage,
}) => {
    const { fieldsMetadata } = useContext(GalleryContext);
    const [selectedTopX, setSelectedTopX] = useState(TOP_X_THRESHOLD);
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const chartRef = useRef(null);
    const dataLength = data?.length || 0;

    useEffect(() => {
        if (!dropdowns.length) return;

        setSelectedMetrics(dropdowns.map((dropdown, index) => dropdown.options?.[index] ?? dropdown.options?.[0]));
    }, [dropdowns]);

    const firstSelectedMetric = selectedMetrics[0];

    const handleDropdownChange = (index, value) => {
        setSelectedMetrics(prev => {
            const updated = [...prev];
            updated[index] = value;
            return updated;
        });
    };

    const headerDropdowns = useMemo(
        () =>
            dropdowns.map((dropdown, index) => ({
                options: dropdown.options,
                label: dropdown.label,
                selected: selectedMetrics[index],
                onChanged: value => handleDropdownChange(index, value),
            })),
        [dropdowns, selectedMetrics]
    );

    // Reset Top X to default when switching dimensions or when data length changes
    useEffect(() => {
        if (dataLength <= TOP_X_THRESHOLD) {
            setSelectedTopX(SHOW_ALL);
        } else {
            setSelectedTopX(TOP_X_THRESHOLD);
        }
    }, [selectedBreakdown, dataLength]);

    // Reset chart zoom when switching metrics, Top X, or dimensions
    useEffect(() => {
        if (chartRef.current?.chart) {
            chartRef.current.chart.zoomOut();
        }
    }, [selectedMetrics, selectedTopX, selectedBreakdown]);

    const isBreakdownChart = !!selectedBreakdown;
    const isTopXDisabled = loading || dataLength <= TOP_X_THRESHOLD;

    const filteredData = useMemo(() => {
        if (!data?.length || !isBreakdownChart || !firstSelectedMetric) return data;

        const metricName = firstSelectedMetric.value || firstSelectedMetric.name;
        const sortedData = [...data].sort((a, b) => (b[metricName] || 0) - (a[metricName] || 0));

        if (selectedTopX === SHOW_ALL) return sortedData;

        return sortedData.slice(0, selectedTopX);
    }, [data, isBreakdownChart, selectedTopX, firstSelectedMetric]);

    const chartOptions = useMemo(() => {
        if (!filteredData?.length) return null;

        if (chartType === SCATTER_CHART) {
            const [xMetric, yMetric] = selectedMetrics;
            if (!xMetric || !yMetric) return null;

            return buildScatterChartOptions({
                data: filteredData,
                xMetric,
                yMetric,
                allMetrics: dropdowns[0]?.options || [],
                fieldsMetadata,
                tooltipFormatter: buildTooltipFormatter?.(xMetric, yMetric),
            });
        }

        const firstMetric = selectedMetrics[0];
        if (!firstMetric) return null;

        return buildCategoryChartOptions({
            chartType,
            data: filteredData,
            firstMetric,
            selectedBreakdown,
            fieldsMetadata,
            config: CHART_TYPE_CONFIG[chartType],
            tooltipFormatter: buildTooltipFormatter?.(firstMetric),
            isMultiColored: hasMultipleCreatives(filteredData),
        });
    }, [filteredData, selectedMetrics, chartType, selectedBreakdown, dropdowns, fieldsMetadata, buildTooltipFormatter]);

    return (
        <div className={css.chartContainer}>
            <CreativeChartHeader
                title={title}
                dropdowns={headerDropdowns}
                breakdownsOptions={breakdownsOptions}
                selectedBreakdown={selectedBreakdown}
                onBreakdownChange={onBreakdownChange}
                breakdownsInputType={breakdownsInputType}
                breakdownsDropdownLabel={breakdownsDropdownLabel}
                onTopXChanged={setSelectedTopX}
                showTopX={showTopX}
                selectedTopX={selectedTopX}
                rowDataLength={dataLength}
                isTopXDisabled={isTopXDisabled}
            />
            <div className={css.chart}>
                {loading ? (
                    <Spinner className={css.spinnerContainer} show />
                ) : chartOptions ? (
                    <HighchartsReact ref={chartRef} key={chartType} highcharts={Highcharts} options={chartOptions} />
                ) : (
                    <EmptyState
                        customIcon={CHART_TYPE_CONFIG[chartType]?.emptyStateIcon}
                        header={hidingChartMessage || CHART_TYPE_CONFIG[chartType]?.emptyStateMessage}
                        containerClass={css.emptyStateContainer}
                        headerClassName={css.chartHeader}
                    />
                )}
            </div>
        </div>
    );
};

CreativeChart.propTypes = {
    dropdowns: PropTypes.arrayOf(
        PropTypes.shape({
            options: PropTypes.arrayOf(PropTypes.object),
            label: PropTypes.string,
        })
    ),
    data: PropTypes.array,
    loading: PropTypes.bool,
    title: PropTypes.string,
    chartType: PropTypes.string,
    breakdownsOptions: PropTypes.array,
    selectedBreakdown: PropTypes.object,
    onBreakdownChange: PropTypes.func,
    breakdownsInputType: PropTypes.oneOf([BreakdownType.TABS, BreakdownType.DROPDOWN]),
    breakdownsDropdownLabel: PropTypes.string,
    showTopX: PropTypes.bool,
    buildTooltipFormatter: PropTypes.func,
    hidingChartMessage: PropTypes.string,
};

export default CreativeChart;
